import React, { useContext, useEffect, useRef, useCallback } from 'react';
import { AppContext } from '@prisjakt/ui-app-context';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { BodyMediumText } from '@prisjakt/ui/text/variants';
import { Row } from '@prisjakt/ui/list/variants';

import { addRecentSearch } from 'src/areas/search-suggestions/history-storage';
import { trackSearchCleared } from 'src/areas/search/tracking';

import {
  StyledSearchBar,
  InputWrapper,
  SearchInput,
  ClearIcon,
  ClearButton,
  SearchIcon,
  SearchButton,
  SearchButtonText,
  Text,
} from './styles';

import { AppState } from 'src/reducers';

type SearchBarProps = {
  query: string;
  setQuery: (query: string) => void;
  clearQuery: () => void;
  placeholderText?: string;
  autoFocus?: boolean;
  autoFocusOnExpand?: boolean;
  className?: string;
  onFocusChange?: () => void;
  selected?: number;
  onBlurChange?: (e: React.MouseEvent) => void;
  onCloseSuggestions: () => void;
  onOpenSuggestions?: () => void;
  onClick?: () => void;
  handleSearchInputRefCallback: (node: React.MutableRefObject<null>) => void;
  handleSearchWrapperRefUpdateCallback: (
    node: React.MutableRefObject<null>
  ) => void;
  isActive?: boolean;
};

const SearchBar = (props: SearchBarProps) => {
  const {
    query,
    setQuery,
    clearQuery,
    className,
    autoFocusOnExpand,
    autoFocus = false,
    onFocusChange,
    selected,
    onBlurChange,
    onCloseSuggestions,
    onOpenSuggestions,
    onClick,
    handleSearchInputRefCallback,
    handleSearchWrapperRefUpdateCallback,
    isActive,
  } = props;

  const { push } = useHistory();
  const { t } = useContext(AppContext);
  const inputElement = useRef<HTMLInputElement | null>(null);
  const searchWrapperElement = useRef<HTMLDivElement | null>(null);
  const focused = useSelector((state: AppState) => state.areas.search.focused);

  const handleKeyInput = (e: KeyboardEvent) => {
    if (e.defaultPrevented) {
      return;
    }
    // Listen for Esc key with fallback for older browsers
    const key = e.key || e.keyCode;
    if (key === 'Escape' || key === 'Esc' || key === 27) {
      blurInput();
      onCloseSuggestions();
    }
  };

  useEffect(() => {
    document.addEventListener('keyup', handleKeyInput);
    return () => {
      document.removeEventListener('keyup', handleKeyInput);
    };
  }, []);

  const focusInput = useCallback((): void => {
    if (inputElement?.current) inputElement.current.focus();
  }, [inputElement]);

  const blurInput = () => {
    if (inputElement?.current) inputElement.current.blur();
  };

  const handleClearSearchClick = useCallback(
    (event: React.MouseEvent): void => {
      event.stopPropagation();
      clearQuery();
      focusInput();
      trackSearchCleared();
    },
    [clearQuery, focusInput]
  );

  const handleSearchInputFocus = useCallback(() => {
    if (onFocusChange) {
      onFocusChange();
    }

    // Open suggestions on focus on mobile only
    if (onOpenSuggestions) {
      onOpenSuggestions();
    }
  }, [onFocusChange, onOpenSuggestions]);

  const handleSearchInputBlur = useCallback(
    e => {
      if (searchWrapperElement.current?.contains(e.relatedTarget)) {
        // Dont blur input when clicking the clear button
        return;
      }

      if (onBlurChange) {
        onBlurChange(e);
      }
    },
    [onBlurChange]
  );

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) =>
      setQuery(event.target.value),
    [setQuery]
  );

  const disabled = query.length < 2;

  const onSearchInputRefCallback = useCallback(
    node => {
      inputElement.current = node;
      handleSearchInputRefCallback(node);
    },
    [handleSearchInputRefCallback, inputElement]
  );

  const onSearchWrapperRefCallback = useCallback(
    node => {
      searchWrapperElement.current = node;
      handleSearchWrapperRefUpdateCallback(node);
    },
    [handleSearchWrapperRefUpdateCallback, searchWrapperElement]
  );

  const handleSubmit = (
    e: React.FormEvent<HTMLFormElement> | React.MouseEvent<HTMLButtonElement>
  ) => {
    e.preventDefault();

    if (disabled || query.length < 2) {
      return;
    }

    addRecentSearch(query);
    onCloseSuggestions();
    push(`/search?search=${encodeURIComponent(query)}`);
  };

  return (
    <StyledSearchBar
      ref={onSearchWrapperRefCallback}
      data-suggestions="searchWrapper"
      data-searchbar="true"
      className={className}
      $focused={Boolean(focused || isActive)}
      $active={Boolean(isActive)}
    >
      <InputWrapper>
        <BodyMediumText>
          {/* form with action required to get the correct submit button on iOS */}
          <form action="/search" onSubmit={handleSubmit} role="search">
            <SearchInput
              ref={onSearchInputRefCallback}
              value={query}
              data-suggestions="search"
              autoFocus={autoFocus || autoFocusOnExpand}
              role="combobox"
              aria-expanded={focused}
              aria-owns={
                focused || isActive ? 'overlaySearchResults' : undefined
              }
              aria-autocomplete="both"
              aria-activedescendant={selected ? `selection${selected}` : ''}
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck={false}
              type="search"
              placeholder={t('search', 'search.placeholder')}
              onFocus={handleSearchInputFocus}
              onBlur={handleSearchInputBlur}
              onChange={handleChange}
              onClick={onClick}
              maxLength={256}
            />
          </form>
        </BodyMediumText>

        <ClearButton
          show={query.length > 0}
          data-test="ClearSearchButton"
          data-testid="clear-search-button"
          onClick={handleClearSearchClick}
        >
          <ClearIcon size={12} />
        </ClearButton>
      </InputWrapper>

      <SearchButton
        disabled={disabled}
        onClick={handleSubmit}
        data-test="SearchButton"
        type="button"
        aria-label={t('header', 'search.button')}
      >
        <SearchButtonText>
          <Row gap={0.5}>
            <SearchIcon size={20} />
            <Text $visible={!disabled}>{t('header', 'search.button')}</Text>
          </Row>
        </SearchButtonText>
      </SearchButton>
    </StyledSearchBar>
  );
};

export default SearchBar;
